import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowLeft, X } from 'lucide-react';

const tourSteps = [
  {
    route: '/dashboard',
    highlightSelector: '[data-testid="active-incident-banner"]',
    title: 'AI at a glance',
    text: 'Every active AI system, every control gap, and every pending decision in one screen. An unauthorized model change was detected 3 hours ago \u2014 8,203 decisions potentially affected.',
  },
  {
    route: '/dashboard/incident-review',
    highlightSelector: '[data-testid="investigation-timeline"]',
    title: 'Complete event reconstruction',
    text: 'Apex reconstructed the full incident timeline automatically \u2014 from the unauthorized change through 8,203 affected decisions to the response package ready in under four minutes.',
  },
  {
    route: '/dashboard/incident-review',
    tabToActivate: 'Control State',
    highlightSelector: '[data-testid="investigation-detail"]',
    title: 'Exact control state at time of event',
    text: 'Two controls were partial when the change occurred. Three were operating normally. Evidence coverage was 100% throughout \u2014 every decision is reconstructable.',
  },
  {
    route: '/dashboard/incident-review',
    tabToActivate: 'Response Package',
    highlightSelector: '[data-testid="total-time-result"]',
    secondarySelector: '[data-testid="generate-response-package"]',
    title: 'Regulator-ready in under four minutes',
    text: 'A complete evidence package for regulators, insurers, legal counsel, and the board \u2014 generated in 3 minutes 47 seconds from investigation open to export-ready.',
  },
  {
    route: '/dashboard/evidence-vault',
    highlightSelector: '[data-testid="evidence-vault"] .bg-\\[\\#10B981\\]\\/5',
    title: 'Every decision signed and immutable',
    text: '47,832 signed Decision Evidence Records. Cryptographically verified every 4 minutes. Tamper-evident. Retention-policy aware. Available for export at any time.',
  },
  {
    route: '/dashboard/assurance',
    highlightSelector: '[data-testid="package-reg"]',
    title: 'One-click audit readiness',
    text: 'Regulator, insurer, board, and internal audit packages \u2014 always current, always generated from live evidence. Completeness shown before you generate so there are no surprises.',
  },
  {
    route: '/dashboard/risk-score',
    highlightSelector: '[data-testid="incident-callout"]',
    title: 'The screen your board sees',
    text: 'AI security posture score, 90-day trend, peer benchmark, top risk factors, and active incident status \u2014 in one screenshot. This is what being in control looks like.',
  },
];

export default function GuidedTour({ onEnd }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [cardPosition, setCardPosition] = useState('bottom');
  const observerRef = useRef(null);
  const retryRef = useRef(null);

  const step = tourSteps[currentStep];

  const findAndHighlight = useCallback(() => {
    if (!step || isComplete) return;

    const findEl = () => {
      let el = document.querySelector(step.highlightSelector);
      if (!el && step.highlightSelector.includes('bg-')) {
        // Fallback for evidence vault emerald banner
        const allBanners = document.querySelectorAll('.bg-\\[\\#10B981\\]\\/5, [class*="10B981"]');
        allBanners.forEach(b => {
          if (b.textContent?.includes('integrity check')) el = b;
        });
      }
      return el;
    };

    const highlight = () => {
      const el = findEl();
      if (el) {
        const rect = el.getBoundingClientRect();
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        setHighlightRect({
          top: rect.top + scrollY,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
        // Position card above or below based on element position
        if (rect.top > window.innerHeight * 0.5) {
          setCardPosition('top');
        } else {
          setCardPosition('bottom');
        }
        // Scroll element into view
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (retryRef.current) clearInterval(retryRef.current);
        return true;
      }
      return false;
    };

    // Try immediately
    if (!highlight()) {
      // Retry up to 3 seconds
      let attempts = 0;
      retryRef.current = setInterval(() => {
        attempts++;
        if (highlight() || attempts > 15) {
          clearInterval(retryRef.current);
        }
      }, 200);
    }
  }, [step, isComplete]);

  // Navigate to the step's route and activate tab if needed
  useEffect(() => {
    if (isComplete || !step) return;
    setIsTransitioning(true);
    setHighlightRect(null);

    const targetRoute = step.route;
    if (location.pathname !== targetRoute) {
      navigate(targetRoute);
    }

    // Wait for route change and DOM settle
    const timer = setTimeout(() => {
      // Click tab if needed
      if (step.tabToActivate) {
        const tabs = document.querySelectorAll('[role="tab"]');
        tabs.forEach(tab => {
          if (tab.textContent?.trim() === step.tabToActivate) tab.click();
        });
      }
      // Highlight after tab switch settles
      setTimeout(() => {
        findAndHighlight();
        setIsTransitioning(false);
      }, 400);
    }, 600);

    return () => {
      clearTimeout(timer);
      if (retryRef.current) clearInterval(retryRef.current);
    };
  }, [currentStep, isComplete, step, navigate, location.pathname, findAndHighlight]);

  const goNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const skipTour = () => {
    onEnd();
  };

  // Completion screen
  if (isComplete) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="tour-completion">
        <div className="absolute inset-0 bg-[#080D1A]" />
        <div className="absolute inset-0 opacity-30" style={{
          background: 'radial-gradient(ellipse at 30% 50%, rgba(99,102,241,0.15), transparent 60%), radial-gradient(ellipse at 70% 50%, rgba(99,102,241,0.1), transparent 60%)',
        }} />
        <div className="relative z-10 text-center max-w-2xl mx-auto px-6">
          <p className="text-2xl sm:text-3xl font-bold text-[#F8FAFC] leading-snug mb-10" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            When AI becomes a board-level event,<br />Apex proves you were in control.
          </p>
          <div className="flex items-center justify-center gap-4 mb-8 flex-wrap">
            <button
              data-testid="tour-explore-btn"
              onClick={() => { navigate('/dashboard'); onEnd(); }}
              className="inline-flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white font-semibold px-8 py-3.5 rounded-xl transition-all shadow-[0_0_30px_rgba(99,102,241,0.3)] hover:shadow-[0_0_50px_rgba(99,102,241,0.5)]"
            >
              Explore the platform <ArrowRight size={18} />
            </button>
            <button
              data-testid="tour-incident-btn"
              onClick={() => { navigate('/dashboard/incident-review'); onEnd(); }}
              className="inline-flex items-center gap-2 border-2 border-white/80 hover:border-white text-white font-semibold px-8 py-3.5 rounded-xl transition-all hover:bg-white/5"
            >
              Start with Incident Review <ArrowRight size={18} />
            </button>
          </div>
          <p className="text-sm text-[#475569]">18 screens &middot; Full platform &middot; All data is simulated for demonstration</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9998]" data-testid="guided-tour-overlay">
      {/* Backdrop - only show when no highlight (highlight creates its own backdrop) */}
      {!highlightRect && <div className="absolute inset-0 bg-black/70" />}

      {/* Highlight cutout with spotlight effect */}
      {highlightRect && (
        <div
          data-testid="tour-highlight"
          className="absolute pointer-events-none"
          style={{
            top: highlightRect.top - 6,
            left: highlightRect.left - 6,
            width: highlightRect.width + 12,
            height: highlightRect.height + 12,
            borderRadius: 12,
            boxShadow: '0 0 0 3px #6366F1, 0 0 20px rgba(99,102,241,0.4), 0 0 0 9999px rgba(0,0,0,0.7)',
            animation: 'tourPulse 2s ease-in-out infinite',
            zIndex: 9998,
          }}
        />
      )}

      {/* Tour Card */}
      <div
        data-testid="tour-card"
        className={`fixed left-1/2 -translate-x-1/2 w-[600px] max-w-[calc(100vw-32px)] z-[9999] transition-all duration-300 ${
          isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
        }`}
        style={{
          ...(cardPosition === 'bottom'
            ? { bottom: 32 }
            : { top: 32 }),
        }}
      >
        <div className="bg-[#0F1629] border-2 border-[#6366F1]/60 rounded-2xl p-6 shadow-2xl shadow-[#6366F1]/10">
          {/* Skip link */}
          <button
            data-testid="tour-skip"
            onClick={skipTour}
            className="absolute top-4 right-4 text-xs text-[#475569] hover:text-[#94A3B8] transition-colors flex items-center gap-1"
          >
            Skip tour <X size={14} />
          </button>

          {/* Content */}
          <h3 className="text-lg font-bold text-[#F8FAFC] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {step?.title}
          </h3>
          <p className="text-sm text-[#94A3B8] leading-relaxed mb-5">
            {step?.text}
          </p>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              data-testid="tour-back"
              onClick={goBack}
              disabled={currentStep === 0}
              className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                currentStep === 0 ? 'text-[#1E2A45] cursor-not-allowed' : 'text-[#64748B] hover:text-[#94A3B8]'
              }`}
            >
              <ArrowLeft size={16} /> Back
            </button>

            <span className="text-xs text-[#475569]">
              Step {currentStep + 1} of {tourSteps.length}
            </span>

            <button
              data-testid="tour-next"
              onClick={goNext}
              className="flex items-center gap-1 bg-[#6366F1] hover:bg-[#818cf8] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all shadow-[0_0_15px_rgba(99,102,241,0.3)]"
            >
              {currentStep < tourSteps.length - 1 ? 'Next' : 'Finish'} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
